import {MC_Constants} from "../MC_Constants";

export class MC_Child_Roster {

    // A ChildRoster is an object used by Parent devices to define
    // which children occupy certain role(s) within the ParentTemplate.

    // Constants
    // *Each field is dynamic (The associated template role ID)*

    // Data
    private entriesMap: Map<string, MC_Role_Entry[]>; // <RoleID, array of child entries>

    // Constructor from JSON data
    constructor(data: any) {
        // Create empty map
        this.entriesMap = new Map();
        // Parse each entry array field
        for (let field in data) {
            // Only parse the non-object unique fields of course
            if (data.hasOwnProperty(field)) {
                // Process the role entries & add them to the map
                const rawArray: any[] = data[field] as any[];
                const entries: MC_Role_Entry[] = rawArray.map((x: any) => new MC_Role_Entry(x));
                this.entriesMap.set(field, entries);
            }
        }
    }

    // Return the array of role IDs (empty if none)
    public getRoleIDs(): string[] {
        return Array.from(this.entriesMap.keys());
    }

    // Return the array of entries for a given role ID (empty if not found)
    public getEntries(roleID: string): MC_Role_Entry[] {
        if (this.entriesMap.has(roleID)) {
            return this.entriesMap.get(roleID)!;
        }
        // Nothing found
        return [];
    }

    // Return an array of all child device IDs
    public getAllChildIDs(): string[] {
        // Get all child entries
        const childEntries: MC_Role_Entry[] = this.getAllChildEntries();
        // Map the entries to device IDs
        return childEntries.map((x) => x.deviceID);
    }

    // Get all child entries
    public getAllChildEntries(): MC_Role_Entry[] {
        const allChildren: MC_Role_Entry[] = [];
        // Add all entries from each role
        for (let roleID of this.getRoleIDs()) {
            const entries: MC_Role_Entry[] = this.getEntries(roleID);
            allChildren.push(...entries);
        }
        // Done
        return allChildren;
    }

    // Return the role ID for a given child ID
    public getRoleIDForChild(childID: string): string | null {
        // Search all roles
        for (let roleID of this.getRoleIDs()) {
            const deviceIDs: string[] = this.getEntries(roleID).map((x) => x.deviceID);
            if (deviceIDs.includes(childID)) {
                // We found the roleID associated with this childID
                return roleID;
            }
        }
        // No role found for this child
        return null;
    }

    // Returns true if this roster already has this child
    public alreadyHasChild(childDeviceID: string): boolean {
        const allChildIDs: string[] = this.getAllChildIDs();
        return (allChildIDs.includes(childDeviceID));
    }

    // Add child to role
    public addChildToRole(
        entry: MC_Role_Entry,
        roleID: string
    ): void {
        // Add child to role, if it isn't already on the roster somewhere
        if (!this.alreadyHasChild(entry.deviceID)) {
            // Update map
            const entries: MC_Role_Entry[] = this.getEntries(roleID);
            entries.push(entry);
            this.entriesMap.set(roleID, entries);
        }
    }

    // Remove child from role
    public removeChildFromRole(
        childDeviceID: string,
        roleID: string
    ): void {
        // Remove device ID from existing entries & update the map
        let entries: MC_Role_Entry[] = this.getEntries(roleID)
            .filter((x) => (x.deviceID !== childDeviceID))
        ;
        this.entriesMap.set(roleID, entries);
    }

}

export class MC_Role_Entry {

    // Fields
    public deviceID: string;
    public deviceType: string;

    // Basic constructor
    constructor(data: any) {
        this.deviceID = MC_Constants.parseNullableField<string>(data, "device_id", "UNKNOWN");
        this.deviceType = MC_Constants.parseNullableField<string>(data, "device_type", "UNKNOWN");
    }

}
